import { Check, Paperclip, Receipt, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { useEmployees } from '@/hooks/employees';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { EXPENSE_TONES, useAdminExpenses, useReopenExpense } from '@/hooks/expenses';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  RecordExpenseDialog,
  ReviewExpenseDialog,
  UploadReceiptDialog,
} from '@/pages/admin/expenses/ExpenseDialogs';
import { formatCents } from '@shared/money';
import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_CATEGORY_VALUES,
  EXPENSE_STATUS_LABELS,
  type AdminExpenseView,
  type ExpenseCategoryValue,
  type ExpenseStatusValue,
} from '@shared/schemas/expense';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import { RECORD_SORTS } from '@shared/schemas/pagination';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/expenses — ROADMAP.md §10.1.
 *
 * **The three cards at the top are real figures and a zero renders as `0`.**
 * That is P1 and P10, and it is the specific thing this phase is asked to get
 * right: the prototype's KPI cards fetch nothing and show `—` forever, which
 * reads as zero at a glance while meaning "unknown". `KpiCard` cannot be handed
 * a dash — the type will not allow it — so the only way to get one here is to
 * pass `null`, which renders a sentence instead.
 *
 * **Every amount on this screen goes through `formatCents`.** The numbers
 * arriving from the API are integer cents and they stay that way until the
 * moment they are drawn.
 *
 * The prototype titles this page "Expense Tracking" while its own nav says
 * "Expenses" — defect P11, one thing with two names. It is "Expenses" here, the
 * same word the sidebar uses.
 */

const STATUS_FILTERS: { value: ExpenseStatusValue | 'all'; label: string }[] = [
  { value: 'pending', label: EXPENSE_STATUS_LABELS.pending },
  { value: 'approved', label: EXPENSE_STATUS_LABELS.approved },
  { value: 'declined', label: EXPENSE_STATUS_LABELS.declined },
  { value: 'all', label: 'All' },
];

export function ExpensesPage() {
  const toast = useToast();

  const [status, setStatus] = useState<ExpenseStatusValue | 'all'>('pending');
  const [category, setCategory] = useState<ExpenseCategoryValue | 'all'>('all');
  /*
   * **These three were already accepted by the API and had no controls.** The
   * list query has taken `employeeId`, `from` and `to` since 10.1 — the screen
   * simply never offered them, so "what did Maya spend in July" was a question
   * only somebody who could write a URL could ask.
   */
  const [employeeId, setEmployeeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');

  const [recording, setRecording] = useState(false);
  const [reviewing, setReviewing] = useState<{
    expense: AdminExpenseView;
    mode: 'approve' | 'decline';
  } | null>(null);
  const [attaching, setAttaching] = useState<AdminExpenseView | null>(null);

  const expenses = useAdminExpenses({
    page,
    sort,
    status,
    category,
    ...(employeeId === '' ? {} : { employeeId }),
    ...(from === '' ? {} : { from }),
    ...(to === '' ? {} : { to }),
  });

  /*
   * The people picker. Capped at the list ceiling, which for a business with
   * eight cleaners is nowhere near — the same call the timesheets filter makes.
   * Inactive people are included, because an expense filed by somebody who has
   * since left is exactly the one being looked for.
   */
  const people = useEmployees({
    sort: 'name',
    page: 1,
    activity: 'all',
    search: '',
    positionId: '',
    skillId: '',
  });

  const filtered = employeeId !== '' || from !== '' || to !== '' || category !== 'all';
  const reopen = useReopenExpense();

  const totals = expenses.data?.totals;

  const columns: Column<AdminExpenseView>[] = [
    {
      key: 'incurredOn',
      header: 'Date',
      render: (row) => <span className="whitespace-nowrap">{formatDay(row.incurredOn)}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      // The label, never the stored value. `deep_cleaning` must not reach a
      // user and neither must `supplies` — P5.
      render: (row) => row.categoryLabel,
    },
    {
      key: 'who',
      header: 'Who',
      render: (row) => (
        <span>
          {row.employeeName ?? <span className="text-ink-500">Business cost</span>}
          {row.employeeName !== null && row.submittedByName !== row.employeeName ? (
            <span className="block text-meta text-ink-500">Entered by {row.submittedByName}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'What for',
      render: (row) => row.description ?? <span className="text-ink-500">Not recorded</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">{formatCents(row.amountCents)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="space-y-1">
          <StatusBadge label={row.statusLabel} tone={EXPENSE_TONES[row.status]} />
          {row.reviewNote ? <p className="text-meta text-ink-500">{row.reviewNote}</p> : null}
        </div>
      ),
    },
    {
      key: 'receipt',
      header: 'Receipt',
      render: (row) =>
        row.hasReceipt ? (
          <a
            // A plain link, because the response is a download with a neutral
            // content type — the browser saves it rather than rendering it.
            href={`/api/admin/expenses/${row.id}/receipt`}
            className="inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline"
          >
            <Paperclip aria-hidden="true" className="size-4" />
            {row.receiptFilename ?? 'Receipt'}
          </a>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAttaching(row);
            }}
          >
            Attach
          </Button>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      hideLabelOnCard: true,
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          {row.status === 'pending' ? (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setReviewing({ expense: row, mode: 'approve' });
                }}
              >
                <Check aria-hidden="true" className="size-4" />
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setReviewing({ expense: row, mode: 'decline' });
                }}
              >
                <X aria-hidden="true" className="size-4" />
                Decline
              </Button>
            </>
          ) : (
            /*
             * Reopening is the visible way to change a decision. Approve and
             * decline deliberately refuse to overwrite each other — a decision
             * carries a date and a name, and quietly replacing one would lose
             * both.
             */
            <Button
              variant="outline"
              size="sm"
              disabled={reopen.isPending}
              onClick={() => {
                reopen.mutate(row.id, {
                  onSuccess: () => {
                    toast.success('Back in the queue.');
                  },
                });
              }}
            >
              <RotateCcw aria-hidden="true" className="size-4" />
              Reopen
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminPage
      title="Expenses"
      description="What the business has spent, and what is waiting to be approved."
      actions={
        <Button
          onClick={() => {
            setRecording(true);
          }}
        >
          <Receipt aria-hidden="true" className="size-4" />
          Record an expense
        </Button>
      }
    >
      {/*
        Three figures, always numbers. `isLoading` shows a skeleton; there is no
        state in which these show a dash. When the answer is zero it says `0`,
        which is a fact rather than an absence.
      */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Waiting to be approved"
          value={totals?.pendingCents ?? null}
          format={formatCents}
          isLoading={expenses.isPending}
          subLabel={
            totals === undefined
              ? undefined
              : `${String(totals.pendingCount)} ${totals.pendingCount === 1 ? 'expense' : 'expenses'}`
          }
        />
        <KpiCard
          label="Approved"
          value={totals?.approvedCents ?? null}
          format={formatCents}
          isLoading={expenses.isPending}
        />
        <KpiCard
          label="Declined"
          value={totals?.declinedCents ?? null}
          format={formatCents}
          isLoading={expenses.isPending}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FilterPills
          label="Filter expenses by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />

        <label className="ml-auto flex items-center gap-2 text-body text-ink-500">
          <span>Category</span>
          <select
            className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body text-ink-900"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as ExpenseCategoryValue | 'all');
              setPage(1);
            }}
          >
            <option value="all">All</option>
            {EXPENSE_CATEGORY_VALUES.map((value) => (
              <option key={value} value={value}>
                {EXPENSE_CATEGORY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-body text-ink-500">
          <span>Who</span>
          <select
            className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body text-ink-900"
            value={employeeId}
            onChange={(event) => {
              setEmployeeId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Anyone</option>
            {(people.data?.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.fullName}
                {person.isActive ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
        </label>

        {/*
          A pair of plain dates rather than the period presets the timesheet and
          reports screens use. Those two are asked about a *period* — a payroll
          week, a month's figures. An expense query is "the trip in March", which
          is a range somebody types.
        */}
        <label className="flex items-center gap-2 text-body text-ink-500">
          <span>From</span>
          <input
            type="date"
            className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body text-ink-900"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
          />
        </label>

        <label className="flex items-center gap-2 text-body text-ink-500">
          <span>To</span>
          <input
            type="date"
            className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body text-ink-900"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
          />
        </label>

        {/*
          **The totals above are the filtered totals**, so a cleared filter is
          the difference between "we spent $80 in March" and "we spent $80".
          Only offered when something is actually set.
        */}
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => {
              setCategory('all');
              setEmployeeId('');
              setFrom('');
              setTo('');
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort expenses"

          options={RECORD_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Expenses, newest cost first"
        columns={columns}
        rows={expenses.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={expenses.isPending}
        error={expenses.error}
        onRetry={() => {
          void expenses.refetch();
        }}
        empty={
          <EmptyState
            icon={Receipt}
            title={
              status === 'pending'
                ? 'Nothing is waiting to be approved.'
                : 'No expenses match these filters.'
            }
            hint="Cleaners submit their own from the employee portal. The office records business costs here."
          />
        }
      />

      <Pagination
        page={page}
        pageSize={expenses.data?.pageSize ?? DEFAULT_PAGE_SIZE}
        total={expenses.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="expenses"
      />

      <RecordExpenseDialog open={recording} onOpenChange={setRecording} />

      <ReviewExpenseDialog
        expense={reviewing?.expense ?? null}
        mode={reviewing?.mode ?? 'approve'}
        onClose={() => {
          setReviewing(null);
        }}
      />

      <UploadReceiptDialog
        expense={attaching}
        onClose={() => {
          setAttaching(null);
        }}
      />
    </AdminPage>
  );
}
